// What the system needs to know about the job workflow, expressed through each step's
// stable key. A step's name and its place in the order are the shop's to change; nothing
// here reads either one to decide what a step means.

export interface WorkflowStep {
  id: string;
  key: string;
  name: string;
  sort: number;
  /** Retired into the pipeline's own steps (Inquiry, Lead, Validated lead, D&E retainer; Spencer, 2026-10-19): kept as a row, left out of every job's checklist. */
  retired_at?: string | null;
}

/** The keys the system gives meaning to. Every other step is just a step. */
export const STEP = {
  /** The opportunity is won: the estimate becomes a job. Production starts here. */
  won: 'won',
  /** The job's Proposal is done: the pipeline step waiting on the job_proposal_done event ticks (Spencer, 2026-10-19). */
  proposal: 'proposal',
  /** Rev 0 is frozen. */
  engineeringLock: 'engineering_lock',
  /** Bennett checks the CAD export against the job's items. Only on the CAD design path. */
  cadCheck: 'cad_check',
  /** Cards are generated and on the board. */
  cardsPulled: 'cards_pulled',
  /** Building and finishing. */
  build: 'build',
  /** Nothing left to do. */
  closed: 'closed',
} as const;

export function stepByKey<T extends WorkflowStep>(steps: T[], key: string): T | undefined {
  return steps.find((s) => s.key === key);
}

/**
 * The workflow in its two halves, in the shop's own order: everything before the step whose
 * key is `won` is the pipeline; that step and everything after it is production.
 */
export function splitWorkflow<T extends WorkflowStep>(steps: T[]): { pipeline: T[]; production: T[] } {
  const sorted = [...steps].sort((a, b) => a.sort - b.sort);
  const at = sorted.findIndex((s) => s.key === STEP.won);
  if (at < 0) return { pipeline: sorted, production: [] };
  return { pipeline: sorted.slice(0, at), production: sorted.slice(at) };
}

/**
 * A step may belong only to jobs with a certain attribute ("only when design_path is cad").
 * The condition is two columns on the step, so which jobs get which steps is data.
 */
export interface StepCondition {
  only_when_attribute?: string | null;
  only_when_value?: string | null;
}

const blankText = (v: unknown) => v === null || v === undefined || String(v).trim() === '';

/** Does this step belong to a job with these attributes? A step with no condition belongs to every job. */
export function stepApplies(step: StepCondition, attrs: Record<string, unknown>): boolean {
  if (blankText(step.only_when_attribute)) return true;
  const have = attrs[String(step.only_when_attribute).trim()];
  if (blankText(have)) return false;
  return String(have).trim().toLowerCase() === String(step.only_when_value ?? '').trim().toLowerCase();
}

/** The workflow one job goes through, in order. */
export function stepsForJob<T extends WorkflowStep & StepCondition>(steps: T[], attrs: Record<string, unknown>): T[] {
  return [...steps].sort((a, b) => a.sort - b.sort).filter((s) => !s.retired_at && stepApplies(s, attrs));
}

/** The attributes a job has not been given yet that some step is waiting on: "design_path". */
export function undecidedAttributes(steps: StepCondition[], attrs: Record<string, unknown>): string[] {
  const out = new Set<string>();
  for (const s of steps) {
    if (blankText(s.only_when_attribute)) continue;
    const key = String(s.only_when_attribute).trim();
    if (blankText(attrs[key])) out.add(key);
  }
  return [...out];
}
